import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {MDIntent} from '@mintdata-wsp/common/components/MDIntent';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {FlowController, FlowExecType} from '@mintdata-wsp/common/controllers/flow/FlowController';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {FunctionResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/FunctionResponse';
import {FunctionSaveResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/FunctionSaveResponse';
import {FunctionsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/FunctionsResponse';
import {FunctionUpdateResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/FunctionUpdateResponse';
import {ValidationErrorDescription} from '@mintdata-wsp/common/controllers/network/generated/responses/ValidationErrorDescription';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    ENTRY_COMPONENT_NAME,
    Flow,
    FlowBlock,
    FlowBounds,
    FlowPoint,
    FlowPort,
    FlowPortKind,
    FlowProperty,
    FlowSerdesContext,
    FlowSimulation,
    FlowSize,
    FlowTick,
    FlowValidationError,
    FlowWire,
    INPUT_COMPONENT_NAME,
    MessageSource,
    OUTPUT_COMPONENT_NAME,
    SimulationId,
    VisualLink
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {FlowStateUtils} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowStateUtils';
import {
    CloudComponent,
    CloudPort,
    CloudProperty,
    CloudTick
} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {TextInputDialogButtonSet, TextInputDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/TextInputDialog';
import {IFunctionArgSpec} from '@mintdata/mint-kit/api/decorators/Function';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import _debounce from 'lodash/debounce';
import {action, IReactionDisposer, reaction} from 'mobx';

export class WspFlowController extends FlowController
{
    private readonly _DEBOUNCE_SAVE_TO_CLOUD_MS = 1000;
    private _flowStateWatchDisposer:IReactionDisposer = null;
    private _flowToSave:Flow | null;

    public ensureFlowsAreLoaded(projectId:number, designId:number, isSample:boolean = false):Promise<boolean>
    {
        const flows = this._getState().flows;

        if( flows.length === 0 )
        {
            return isSample
                ? this.loadSampleFlows(designId)
                : this.loadFlows(projectId, designId);
        }

        return Promise.resolve(true);
    }

    @action
    private loadFlows(projectId:number, designId:number):Promise<boolean>
    {
        return API.functions.list(designId)
            .then(action((response:FunctionsResponse) =>
            {
                const flows = response.functions.map((funcResponse:FunctionResponse) =>
                {
                    const flow = Flow.fromResponse(funcResponse);

                    flow.projectId = projectId;
                    flow.designId = designId;

                    return flow;
                });

                this.setFlows(flows);

                return true;
            }));
    }

    public async refreshFlows(projectId:number, designId:number)
    {
        await this.loadFlows(projectId, designId);
    }

    private _getFlowName(baseName:string = 'FLOW'):string
    {
        const state = this._getState();
        const existingNames:object = state.flows.reduce((acc:object, flow:Flow) =>
        {
            acc[flow.name] = flow.name;
            return acc;
        }, {});

        let i:number = 1;
        let name:string = `${baseName}_${i}`;

        while( existingNames[name] )
        {
            name = `${baseName}_${++i}`;
        }

        return name;
    }

    @action
    private loadSampleFlows(designId:number):Promise<boolean>
    {
        return API.sample.sampleFunctionsList(designId)
            .then(action((response:FunctionsResponse) =>
            {
                const flows = response.functions.map((funcResponse:FunctionResponse) =>
                {
                    const flow = Flow.fromResponse(funcResponse);

                    flow.projectId = null;
                    flow.designId = designId;

                    return flow;
                });

                this.setFlows(flows);

                return true;
            }));
    }

    @action
    public createFlowAndNavigate(projectId:number, designId:number):void
    {
        const {navigation} = controllers;

        const name = this._getFlowName();
        const flowBlocks = [
            this._createFlowEntryBlock(),
            this._createFlowInputBlock(new FlowPoint(200, 200), 'param1'),
            this._createFlowInputBlock(new FlowPoint(200, 400), 'param2'),
            this._createFlowOutputBlock(new FlowPoint(700, 300))
        ];

        this.createFlow(projectId, designId, name, flowBlocks).then((flow:Flow) =>
        {
            navigation.toFlowEditor(projectId, designId, flow.id);
            requestAnimationFrame(() => this.renameFlowDialog(flow));
        });
    }

    @action
    public duplicateFlowAndNavigate(flow:Flow):void
    {
        const {navigation} = controllers;
        const {projectId, designId, name, flowBlocks} = flow;

        const newName = this._getFlowName(name);
        const newBlocks = flowBlocks.map(block => block.clone());

        const cloneCtx = new FlowSerdesContext();
        newBlocks.map(block => cloneCtx.putBlock(block));

        newBlocks.map((newBlock, index) =>
        {
            const oldBlock = flowBlocks[index];

            newBlock.inWires = oldBlock.inWires.map(wire => wire.clone(cloneCtx));
            newBlock.outWires = oldBlock.outWires.map(wire => wire.clone(cloneCtx));
        });

        this.createFlow(projectId, designId, newName, newBlocks).then((flow:Flow) =>
        {
            navigation.toFlowEditor(flow.projectId, flow.designId, flow.id);
            requestAnimationFrame(() => this.renameFlowDialog(flow));
        });
    }

    @action
    public createFlow(projectId:number, designId:number, name:string, flowBlocks:Array<FlowBlock>):Promise<Flow>
    {
        const state = this._getState();
        state.isNewFlowCreating = true;

        const newFlow = new Flow(null, projectId, designId, name, flowBlocks);
        return API.functions.create(designId, newFlow.toCreateRequest())
            .then(action((response:FunctionSaveResponse | ApiError) =>
            {
                if( response instanceof ApiError )
                {
                    if( response.code === 409 )
                    {
                        controllers.ui.dialogs.showDangerReload('Oops', response.message,
                            () =>
                            {
                                controllers.navigation.toDesign(projectId, designId);
                                window.location.reload();
                            }, MDIcons.common.Info);
                    }
                    else
                    {
                        console.error(response.message);
                    }
                    state.isNewFlowCreating = false;
                }
                else
                {
                    newFlow.id = response.functionId;
                    const flows = this._getState().flows;
                    flows.unshift(newFlow);
                    controllers.ui.openDesign.getOpenDesign().designRef.version = response.designVersion;
                    state.isNewFlowCreating = false;
                    return newFlow;
                }
            }))
            .catch(() =>
            {
                state.isNewFlowCreating = false;
                return null;
            });
    }

    @action
    public saveOpenFlow():Promise<Flow>
    {
        const flow = this.getOpenFlow();
        if( this._flowToSave && this._flowToSave.designId === flow.designId && this._flowToSave.projectId === flow.projectId )
        {
            console.warn(`Skipped, waiting for save flow with design version: ${rootState.ui.visualDesigner.openDesign.designRef.version}`);
            return;
        }
        this._flowToSave = this.getOpenFlow();
        let updateFunctionRequest = flow.toUpdateRequest();
        const projectId = controllers.ui.openDesign.getOpenDesign().designRef.projectId;
        const designId = controllers.ui.openDesign.getOpenDesign().designRef.designId;
        return API.functions.save(flow.designId, flow.id, updateFunctionRequest)
            .then((response:FunctionUpdateResponse | ApiError) =>
            {
                this._flowToSave = null;
                if( response instanceof ApiError )
                {
                    if( response.code === 409 )
                    {
                        controllers.ui.dialogs.showDangerReload('Oops', response.message,
                            () =>
                            {
                                controllers.navigation.toDesign(flow.projectId, flow.designId);
                                window.location.reload();
                            }, MDIcons.common.Info);
                    }
                    else
                    {
                        console.error(`Error saving flow: ${response.message}`);
                    }
                }
                else
                {
                    let designRef = controllers.ui.openDesign.getOpenDesign().designRef;
                    if( projectId === designRef.projectId && designId === designRef.designId )
                    {
                        designRef.version = response.designVersion;
                    }
                    return flow;
                }
            }).catch(reason =>
            {
                this._flowToSave = null;
                return null;
            });
    }

    @action
    public deleteFlow(flow:Flow):void
    {
        const {navigation, ui} = controllers;
        const {flowSimulation} = ui;

        const flows = this.getFlows();

        API.functions.delete(flow.designId, flow.id)
            .then((response:any) =>
            {
                if( ApiError.isError(response) )
                {
                    throw new Error(response.massage);
                }

                return Promise.all(
                    flowSimulation.getAll()
                        .filter((simulation) => (
                            simulation.functionId === flow.id && !flowSimulation.isStoppedOrInitialNotRunning(simulation.id)
                        ))
                        .map((simulation) =>
                        {
                            return (
                                simulation.isForFlowEditor
                                    ? this.stopFlowFromEditor(flow)
                                    : this.stopCloudFunctionFromSlot(simulation.slot.id)
                            );
                        })
                );
            })
            .then(() => flowSimulation.remove((simulation) => simulation.functionId === flow.id))
            .then(action(() =>
            {
                const flowIndex = flows.findIndex(({id}) => id === flow.id);
                flows.splice(flowIndex, 1);

                if( this._getState().openFlow.id !== flow.id )
                {
                    return;
                }

                if( flows.length > 0 )
                {
                    navigation.toFlowEditor(flow.projectId, flow.designId, flows[0].id);
                }
                else
                {
                    navigation.toDesign(flow.projectId, flow.designId);
                }
            }))
            .catch(() =>
            {
                // todo:
            });
    }

    @action
    public openFlow(flowId:number):boolean
    {
        const {flowSelection} = controllers.ui;

        flowSelection.clearSelectedBlocks();
        flowSelection.clearSelectedWire();

        const flows = this.getFlows();
        const flowIndex = flows.findIndex(({id}) => id === flowId);

        if( flowIndex === -1 )
        {
            return false;
        }

        const flowsWithCurrentOnTop = ArrayUtils.reorderArray(flows, flowIndex, 0);

        this._getState().flows = flowsWithCurrentOnTop;
        this.setOpenFlow(flowsWithCurrentOnTop[0]);
        this._startWatchingFlowState();

        return true;
    }

    @action
    public execFlowFromEditor(flow:Flow, inputValues:Map<string, string>, execType:FlowExecType = FlowExecType.REST):Promise<boolean>
    {
        const {
            flowSimulation,
            flowSimulationStatus,
            flowSimulationMessages,
            flowValidation,
            flowCloud,
            panels,
            visualData
        } = controllers.ui;

        let simulation = flowSimulation.getForFlowEditor(flow.id);

        //simulation is active => return immidiately
        if( simulation && !flowSimulation.isStoppedOrInitialNotRunning(simulation.id) )
            return Promise.resolve(false);

        if( !simulation )
        {
            simulation = FlowSimulation.forFlowEditor(flow);
            flowSimulation.add(simulation);
        }
        else
        {
            simulation.regenerateId();
        }

        simulation.execType = execType;

        const simulationId = simulation.id;

        flowSimulationStatus.onUserStartingTopology(simulationId);
        flowSimulationMessages.clearAll(simulationId);
        visualData.clearAll(flow);

        return Promise.resolve()
            .then(this._logFlowProgress(simulationId, 'Starting flow...'))
            .then(() =>
            {
                const flowErrors = flowValidation.validate(flow.id);

                if( flowErrors.length > 0 )
                {
                    flowSimulationStatus.onValidationErrors(simulationId);
                    flowSimulationMessages.logFlowErrors(simulationId, flowErrors);
                    panels.problems.selectPanel();

                    throw new Error(`Flow topology is not valid`);
                }
            })
            .then(this._logFlowProgress(simulationId, 'Starting topology...'))
            .then(() =>
            {
                const parameterValues = this._createParameterValuesFrame(flow, inputValues);

                if( execType === FlowExecType.REST )
                {
                    return flowCloud.executeFunction(flow.id, simulationId, parameterValues);
                }
                else if( execType === FlowExecType.Stream )
                {
                    return flowCloud.executeStream(flow.id, simulationId, parameterValues);
                }
            })
            .then(this._logFlowProgress(simulationId, 'Starting topology... done'))
            .then((response:{success:boolean, result:any, errorMessage:string}) =>
            {
                if( response && !response.success )
                {
                    const err = response.errorMessage || 'Flow execution unknown error';

                    flowSimulationStatus.onFailedToStartErrors(simulationId);

                    throw new Error(err);
                }
            })
            .then(this._logFlowProgress(simulationId, 'Starting flow... done'))
            .then(() => true)
            .catch((err) =>
            {
                flowSimulationMessages.logError(simulationId, `An error occurred during flow startup. ${err ? err : ''}`, MessageSource.UI);

                if( !flowSimulation.isStoppedOrInitialNotRunning(simulationId) )
                {
                    flowSimulationStatus.onFailedToStartErrors(simulationId);
                }

                return false;
            });
    }

    protected _logFlowProgress(id:SimulationId, msg:string):(arg?:any) => any
    {
        return (arg?:any):any =>
        {
            commonControllers.flowSimulationMessages.logMessage(id, msg);

            return arg;
        };
    }

    @action
    public stopFlowFromEditor(flow:Flow):Promise<boolean>
    {
        const {flowSimulation, flowSimulationMessages} = controllers.ui;

        const simulation = flowSimulation.getForFlowEditor(flow.id);

        if( !simulation )
            return Promise.resolve(false);

        return this.stopCloudFunctionExecution(
            simulation.id,
            (id, msg) => flowSimulationMessages.logMessage(id, msg),
            (id, msg) => flowSimulationMessages.logError(id, msg, MessageSource.TopologyControl)
        );
    }

    @action
    public performFlowEditorCleanup():Promise<any>
    {
        return this._stopSimulations(simulation => simulation.isForFlowEditor);
    }

    @action
    public stopAllSimulations():Promise<any>
    {
        return this._stopSimulations();
    }

    private _stopSimulations(simulationFilterFn?:(FlowSimulation) => boolean):Promise<any>
    {
        const {flowSimulation} = controllers.ui;

        const stopPromises = flowSimulation.getAll()
            .filter((simulation) => (!simulationFilterFn || simulationFilterFn(simulation)) && !flowSimulation.isStoppedOrInitialNotRunning(simulation.id))
            .map((simulation) => this._stopSimulation(simulation));

        return Promise.all(stopPromises);
    }

    @action
    private _stopSimulation(simulation:FlowSimulation):Promise<boolean>
    {
        const {flowSimulationMessages} = controllers.ui;

        if( !simulation )
            return Promise.resolve(false);

        return this.stopCloudFunctionExecution(
            simulation.id,
            (id, msg) => flowSimulationMessages.logMessage(id, msg),
            (id, msg) => flowSimulationMessages.logError(id, msg, MessageSource.TopologyControl)
        );
    }

    @action
    public setOpenFlow(flow:Flow):void
    {
        this._getState().openFlow = flow;
    }

    public isNewFlowCreating():boolean
    {
        return this._getState().isNewFlowCreating;
    }

    public createNewFlowBlock(component:CloudComponent,
                              bounds:FlowBounds,
                              isDebugBlock:boolean = false,
                              isEntryBlock:boolean = false,
                              visualLink:VisualLink):FlowBlock
    {
        const newBlockId = IdUtil.newGUID();
        const {properties, ticks, inputPorts, outputPorts} = this._createNewBlockParts(component);

        return new FlowBlock(
            newBlockId,
            bounds,
            isDebugBlock,
            isEntryBlock,
            visualLink,
            component,
            properties,
            ticks,
            inputPorts,
            outputPorts
        );
    }

    public getOpenFlow():Flow | null
    {
        const {openFlow} = this._getState();

        return openFlow ? openFlow : null;
    }

    public renameFlowDialog(flow:Flow):void
    {
        const props = new TextInputDialogProps(
            MDIntent.Default,
            `Rename flow "${flow.name}"`,
            null,
            '',
            'Enter flow name',
            flow.name,
            null,
            TextInputDialogButtonSet.OK_CANCEL,
            this._getRenameFlowConfirmHandler(flow),
            null,
            true,
            this._getRenameFlowChangeHandler(flow),
            this._getRenameFlowErrorMessageHandler(flow)
        );

        this._clearRenameFlowErrorMessage(flow.id);
        controllers.ui.dialogs.showDialog(Dialogs.TextInput, true, props);
    }

    public deleteFlowDialog(flow:Flow):void
    {
        controllers.ui.dialogs.showDangerConfirm(`Delete Flow?`,
            `${flow.name}`,
            this._getDeleteFlowConfirmHandler(flow),
            ['Cancel', 'DELETE']
        );
    }

    @action
    public setFlowLastInputValues(flowId:number, values:Map<string, string>):boolean
    {
        const flow = this.getFlowById(flowId);

        if( !flow )
        {
            return false;
        }

        flow.lastInputValues = values;

        return true;
    }

    public getFlowLastInputValues(flowId:number):Map<string, string>
    {
        const flow = this.getFlowById(flowId);

        return flow ? flow.lastInputValues : new Map();
    }

    @action
    public clearFlowLastInputValues(flowId:number):boolean
    {
        const flow = this.getFlowById(flowId);

        if( !flow )
        {
            return false;
        }

        flow.lastInputValues = new Map();

        return true;
    }

    public hasFlowErrors(flowId:number):boolean
    {
        return this.getFlowErrors(flowId).length > 0;
    }

    public getFlowErrors(flowId:number):FlowValidationError[]
    {
        const flow = this.getFlowById(flowId);

        return flow ? flow.flowErrors : [];
    }

    @action.bound
    public clearFlowErrors(flowId:number):boolean
    {
        const flow = this.getFlowById(flowId);

        if( !flow )
        {
            return false;
        }

        flow.flowErrors = [];

        return true;
    }

    @action.bound
    public setFlowErrors(flowId:number, flowErrors:FlowValidationError[]):boolean
    {
        const flow = this.getFlowById(flowId);

        if( !flow )
        {
            return false;
        }

        flow.flowErrors = flowErrors;

        return true;
    }

    public hasCloudFlowErrors(flowId:number):boolean
    {
        return this.getCloudFlowErrors(flowId).length > 0;
    }

    public getCloudFlowErrors(flowId:number):ValidationErrorDescription[]
    {
        const flow = this.getFlowById(flowId);

        return flow ? flow.cloudFlowErrors : [];
    }

    @action.bound
    public clearCloudFlowErrors(flowId:number):boolean
    {
        const flow = this.getFlowById(flowId);

        if( !flow )
        {
            return false;
        }

        flow.cloudFlowErrors = [];

        return true;
    }

    @action.bound
    public setCloudFlowErrors(flowId:number, cloudErrors:ValidationErrorDescription[]):boolean
    {
        const flow = this.getFlowById(flowId);

        if( !flow )
        {
            return false;
        }

        flow.cloudFlowErrors = cloudErrors;

        return true;
    }

    private _getDeleteFlowConfirmHandler = (flow:Flow) =>
    {
        return () => this.deleteFlow(flow);
    };

    @action
    private _setRenameFlowErrorMessage(flowId:number, message:string):boolean
    {
        const flow = this.getFlowById(flowId);

        if( !flow )
        {
            return false;
        }

        flow.renameErrorMessage = message;

        return true;
    }

    @action
    private _clearRenameFlowErrorMessage(flowId:number):boolean
    {
        const flow = this.getFlowById(flowId);

        if( !flow )
        {
            return false;
        }

        flow.renameErrorMessage = null;

        return true;
    }

    private _getRenameFlowErrorMessageHandler = (flow:Flow) =>
    {
        return () =>
        {
            return flow.renameErrorMessage;
        };
    };

    private _getRenameFlowChangeHandler = (flow:Flow) =>
    {
        return (newValue:string) =>
        {
            this._clearRenameFlowErrorMessage(flow.id);
        };
    };

    private _getRenameFlowConfirmHandler = (flow:Flow) =>
    {
        const {dialogs} = controllers.ui;

        return (newName:string) =>
        {
            if( !newName ) return;

            this._clearRenameFlowErrorMessage(flow.id);

            const {designId} = flow;
            const flowUpdateRequest = flow.toUpdateRequest();

            (flowUpdateRequest as any).name = newName;

            API.functions.save(designId, flow.id, flowUpdateRequest)
                .then(action((response:any) =>
                {
                    if( ApiError.isError(response) )
                    {
                        this._setRenameFlowErrorMessage(flow.id, (response as ApiError).message);
                    }
                    else
                    {
                        const resp = response as FunctionUpdateResponse;
                        flow.name = newName;
                        controllers.ui.openDesign.getOpenDesign().designRef.version = response.designVersion;
                        dialogs.hideDialog(Dialogs.TextInput);
                    }
                }));

            return false;
        };
    };

    private _createFlowEntryBlock():FlowBlock
    {
        const {artifacts} = controllers.ui;

        const cloudEntryComponent = artifacts.getCloudComponentByName(ENTRY_COMPONENT_NAME);
        const flowPoint = new FlowPoint(0, 0);
        const flowBounds = new FlowBounds(flowPoint, FlowSize.Unknown);

        return this.createNewFlowBlock(cloudEntryComponent, flowBounds, false, true, null);
    }

    private _createFlowInputBlock(dropAt:FlowPoint, instanceName:string):FlowBlock
    {
        const {artifacts} = controllers.ui;

        const cloudInputComponent = artifacts.getCloudComponentByName(INPUT_COMPONENT_NAME);
        const flowBounds = new FlowBounds(dropAt, FlowSize.Unknown);

        const flowBlock = this.createNewFlowBlock(cloudInputComponent, flowBounds, false, false, null);

        flowBlock.instanceName = instanceName;

        return flowBlock;
    }

    private _createFlowOutputBlock(dropAt:FlowPoint):FlowBlock
    {
        const {artifacts} = controllers.ui;

        const cloudEntryComponent = artifacts.getCloudComponentByName(OUTPUT_COMPONENT_NAME);
        const flowBounds = new FlowBounds(dropAt, FlowSize.Unknown);

        return this.createNewFlowBlock(cloudEntryComponent, flowBounds, false, false, null);
    }

    private _createNewBlockParts(component:CloudComponent):{properties:FlowProperty[], ticks:FlowTick[], inputPorts:FlowPort[], outputPorts:FlowPort[]}
    {
        const properties = component.properties.map((cloudProp:CloudProperty) => new FlowProperty(cloudProp));

        const inputPorts:FlowPort[] = component.inputPorts.map((p:CloudPort) =>
        {
            const newPortId = IdUtil.newGUID();
            return new FlowPort(newPortId, FlowPortKind.InputPort, p);
        });

        const outputPorts:FlowPort[] = component.outputPorts.map((p:CloudPort) =>
        {
            const newPortId = IdUtil.newGUID();
            return new FlowPort(newPortId, FlowPortKind.OutputPort, p);
        });

        const ticks = component.ticks.map((t:CloudTick) =>
        {
            const flowTick = new FlowTick(t);

            flowTick.interval = t.interval;
            flowTick.unit = t.units;

            return flowTick;
        });

        return {properties, ticks, inputPorts, outputPorts};
    }

    private _createParameterValuesFrame(flow:Flow, inputValues:Map<string, string>):IFrame
    {
        const funcArgs:IFunctionArgSpec[] = FlowStateUtils.generateFuncArgSpecs(flow.inputs, false);
        const fields = funcArgs.map(({title, type}) => new FrameField(title, type as Type));

        const row:IValue[] = funcArgs.map(({title, type}) =>
        {
            const value:string = inputValues.get(title);

            return CoercionUtils.coerce(AString.fromString(value), type as Type, null);
        });

        return Frames.fromFieldsAndData(fields, [row], null);
    }

    private _startWatchingFlowState = ():void =>
    {
        if( this._flowStateWatchDisposer )
        {
            this._flowStateWatchDisposer();
            this._flowStateWatchDisposer = null;
        }

        this._flowStateWatchDisposer = reaction(
            this._touchFlowState,
            _debounce(this._onFlowStateChanged, this._DEBOUNCE_SAVE_TO_CLOUD_MS));
    };

    private _touchFlowState = ():any =>
    {
        const flow = this.getOpenFlow();
        const {flowBlocks} = flow;

        return {
            tocuhA: flowBlocks.map(this._touchFlowBlock)
        };
    };

    private _touchFlowBlock = (fb:FlowBlock):any =>
    {
        return {
            touch0: fb.bounds,
            touch1: fb.bounds.position,
            touch2: fb.bounds.size,
            touch3: fb.instanceName,
            touch4: fb.inWires.map((w:FlowWire) => (w.id)),
            touch5: fb.outWires.map((w:FlowWire) => (w.id)),
            touch6: fb.properties.map((p:FlowProperty) => ({value: p.value, meta: p.meta}))
        };
    };

    private _onFlowStateChanged = () =>
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        if( sampleDesign ) return;

        this.saveOpenFlow();
    };
}
